// HTTP-Grundlage für alle API-Aufrufe (Fundament). Cookie-Anmeldung, gleiche Herkunft.
import type { FehlerAntwort } from "./types";

/** Fehler aus der API in der einheitlichen Form (API.md, Abschnitt 2). */
export class ApiFehler extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiFehler";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Wird ausgelöst, wenn die Sitzung fehlt oder abgelaufen ist (Anmeldeseite zeigen). */
export const ABGEMELDET_EREIGNIS = "crm:abgemeldet";

type Parameter = Record<string, string | number | boolean | null | undefined | string[]>;

export function abfrage(params?: Parameter): string {
  if (!params) return "";
  const teile = new URLSearchParams();
  for (const [schluessel, wert] of Object.entries(params)) {
    if (wert === undefined || wert === null || wert === "") continue;
    if (Array.isArray(wert)) {
      if (wert.length) teile.set(schluessel, wert.join(","));
    } else {
      teile.set(schluessel, String(wert));
    }
  }
  const text = teile.toString();
  return text ? `?${text}` : "";
}

async function anfrage<T>(methode: string, pfad: string, inhalt?: unknown, signal?: AbortSignal): Promise<T> {
  let antwort: Response;
  try {
    antwort = await fetch(pfad, {
      method: methode,
      credentials: "same-origin",
      headers: inhalt === undefined ? { Accept: "application/json" } : { Accept: "application/json", "Content-Type": "application/json" },
      body: inhalt === undefined ? undefined : JSON.stringify(inhalt),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiFehler(0, "keine_verbindung", "Keine Verbindung zum CRM-Server.");
  }
  if (antwort.status === 204) return undefined as T;
  const text = await antwort.text();
  let daten: unknown = undefined;
  if (text) {
    try {
      daten = JSON.parse(text);
    } catch {
      daten = undefined;
    }
  }
  if (!antwort.ok) {
    const fehler = (daten as FehlerAntwort | undefined)?.error;
    const code = fehler?.code ?? (antwort.status === 404 ? "nicht_gefunden" : "fehler");
    const meldung = fehler?.message ?? `Anfrage fehlgeschlagen (${antwort.status}).`;
    if (antwort.status === 401 && code === "nicht_angemeldet") {
      window.dispatchEvent(new CustomEvent(ABGEMELDET_EREIGNIS));
    }
    throw new ApiFehler(antwort.status, code, meldung, fehler?.details ?? {});
  }
  return daten as T;
}

export const http = {
  get: <T>(pfad: string, params?: Parameter, signal?: AbortSignal) => anfrage<T>("GET", pfad + abfrage(params), undefined, signal),
  post: <T>(pfad: string, inhalt?: unknown) => anfrage<T>("POST", pfad, inhalt ?? {}),
  put: <T>(pfad: string, inhalt?: unknown) => anfrage<T>("PUT", pfad, inhalt ?? {}),
  patch: <T>(pfad: string, inhalt?: unknown) => anfrage<T>("PATCH", pfad, inhalt ?? {}),
  del: <T>(pfad: string) => anfrage<T>("DELETE", pfad),
};

/** Kurzer Text für die Anzeige eines beliebigen Fehlers. */
export function fehlerText(e: unknown): string {
  if (e instanceof ApiFehler) return e.message;
  if (e instanceof Error) return e.message;
  return "Unbekannter Fehler.";
}
